import { motion } from "framer-motion";
import { Tv, Film, Clapperboard, Radio, Monitor, Users } from "lucide-react";

const mediaImages = [
  {
    url: "https://images.unsplash.com/photo-1682130301125-5b63bbf93241?w=800&q=80",
    label: "CINEMA",
  },
  {
    url: "https://images.unsplash.com/photo-1684159235233-2e93b5ff0e7b?w=800&q=80",
    label: "PRODUZIONI TV",
  },
  {
    url: "https://images.pexels.com/photos/2608517/pexels-photo-2608517.jpeg?auto=compress&cs=tinysrgb&w=800",
    label: "VIDEO PRODUCTION",
  },
  {
    url: "https://images.unsplash.com/photo-1552694062-ccf53289a8da?w=800&q=80",
    label: "SET CINEMATOGRAFICO",
  },
  {
    url: "https://images.pexels.com/photos/3945313/pexels-photo-3945313.jpeg?auto=compress&cs=tinysrgb&w=800",
    label: "BROADCASTING",
  },
  {
    url: "https://images.unsplash.com/photo-1643651474048-4377131e82da?w=800&q=80",
    label: "SERIE TV",
  },
  {
    url: "https://images.pexels.com/photos/106344/pexels-photo-106344.jpeg?auto=compress&cs=tinysrgb&w=800",
    label: "SOCIAL MEDIA TV",
  },
];

const specializations = [
  { icon: Tv, title: "Trasmissioni TV", desc: "Gestione social per programmi televisivi in onda sui principali canali nazionali" },
  { icon: Film, title: "Serie TV", desc: "Strategie social per serie TV: hype, lancio, engagement e community management" },
  { icon: Clapperboard, title: "Cinema", desc: "Promozione digitale di produzioni cinematografiche su tutti i canali social" },
  { icon: Radio, title: "Live Broadcasting", desc: "Copertura social in tempo reale per eventi live, premiere e backstage" },
  { icon: Monitor, title: "Canali TV Nazionali", desc: "Collaborazione con Canale 5, Italia 1, Rete 4 per campagne integrate" },
  { icon: Users, title: "Community & Fan Base", desc: "Costruzione e gestione di community dedicate a show, film e talent" },
];

function MediaCarousel({ images, speed = 35, direction = "left" }) {
  const doubled = [...images, ...images];
  return (
    <div className="overflow-hidden">
      <div
        className={direction === "left" ? "animate-carousel-left" : "animate-carousel-right"}
        style={{ animationDuration: `${speed}s` }}
      >
        <div className="flex gap-4" style={{ width: "max-content" }}>
          {doubled.map((img, i) => (
            <div key={i} className="group relative w-[300px] sm:w-[380px] h-[210px] sm:h-[250px] flex-shrink-0 overflow-hidden border border-white/10">
              <img src={img.url} alt={img.label} className="w-full h-full object-cover transition-all duration-700 grayscale-[50%] group-hover:grayscale-0 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/90 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between">
                <span className="font-mono text-[10px] tracking-[0.25em] text-white/90">{img.label}</span>
                <span className="w-6 h-[1px] bg-[#FF3366]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MediaSocialSection() {
  const firstRow = mediaImages.slice(0, 4);
  const secondRow = mediaImages.slice(4, 7);

  return (
    <section data-testid="media-social-section" id="media-social" className="py-24 sm:py-32 bg-[#0a0a0a] border-y border-white/5 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="font-mono text-xs tracking-[0.2em] text-[#FF3366] uppercase">SOCIAL MEDIA ENTERTAINMENT</span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4 leading-tight">
            Social Media per<br />
            <span className="text-[#A3A3A3]">TV, Serie TV & Cinema</span>
          </h2>
          <p className="text-[#A3A3A3] font-body text-lg mt-6 max-w-2xl">
            Gestiamo i social media specializzati nel mondo dell'intrattenimento: trasmissioni televisive, serie TV e produzioni cinematografiche. Creiamo engagement, hype e community per ogni produzione.
          </p>
        </motion.div>
      </div>

      <div className="space-y-4">
        <MediaCarousel images={firstRow} speed={36} direction="left" />
        <MediaCarousel images={secondRow} speed={32} direction="right" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 mt-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {specializations.map((s, i) => (
            <div
              key={i}
              data-testid={`media-spec-${i}`}
              className="border border-white/10 bg-[#121212] p-6 hover:border-[#FF3366]/40 transition-all duration-300 group"
            >
              <s.icon size={24} className="text-[#FF3366] mb-3 group-hover:scale-110 transition-transform" />
              <h4 className="font-heading text-lg font-bold">{s.title}</h4>
              <p className="text-[#A3A3A3] text-sm mt-2 font-body">{s.desc}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
